/**
 * Common types used across LOCI applications
 */

import type { CanonicalCategorySlug } from '../ingestion/category-taxonomy';

// Base entity types
export interface BaseEntity {
  id: string;
  createdAt: Date;
  updatedAt: Date;
}

// User-related types
export interface User extends BaseEntity {
  email: string;
  username?: string;
  firstName?: string;
  lastName?: string;
  avatar?: string;
  verified: boolean;
  preferences?: UserPreferences;
}

/**
 * Privacy-related user preferences.
 * Mirrors `userPreferencesSchema.privacy` in @loci/validation.
 */
export interface UserPrivacyPreferences {
  /** Whether the user's profile is visible to other users. */
  profileVisible: boolean;
  /** Whether the user's activity (e.g. event attendance) is shown publicly. */
  showActivity: boolean;
  /** Whether location-based features (nearby events, radius search) are enabled. */
  locationEnabled?: boolean;
  /** Whether anonymous usage analytics are collected. */
  analyticsEnabled?: boolean;
  /** Whether browsing history is tracked for personalized recommendations. */
  trackBrowsing?: boolean;
}

export const THEME_PREFERENCES = ['light', 'dark', 'system'] as const;
export type ThemePreference = (typeof THEME_PREFERENCES)[number];
export const DEFAULT_THEME_PREFERENCE: ThemePreference = 'system';

/**
 * Full user preferences object stored per-user.
 * Validated at the API boundary by `userPreferencesSchema` from @loci/validation.
 */
export interface UserPreferences {
  /** Notification channel preferences. */
  notifications: {
    /** Receive notifications via email. */
    email: boolean;
    /** Receive push notifications. */
    push: boolean;
    /** Receive SMS notifications. */
    sms: boolean;
    /** Push reminders before saved events. */
    pushReminders?: boolean;
    /** Push alerts when ticket prices drop. */
    pushPriceDrops?: boolean;
    /** Push updates for followed categories. */
    pushCategoryUpdates?: boolean;
    /** Enable quiet hours (suppresses push during the window). */
    quietHoursEnabled?: boolean;
    /** Quiet hours start time in HH:mm (24h) format, e.g. "22:00". */
    quietHoursStart?: string;
    /** Quiet hours end time in HH:mm (24h) format, e.g. "07:00". */
    quietHoursEnd?: string;
    /** Receive a periodic email digest of saved events. */
    emailDigest?: boolean;
    /** Receive email updates for saved events (price/availability changes). */
    emailSavedUpdates?: boolean;
  };
  /** Privacy preferences. */
  privacy: UserPrivacyPreferences;
  /** User's preferred event categories (e.g. ["music", "food"]). */
  preferredCategories?: string[];
  /** Default search radius in kilometers. */
  radiusKm?: number;
  /**
   * UI theme choice (API-1659 / O-7). Stored on `User.preferences.theme`.
   * Default `system` when omitted. The `loci-theme` cookie carries the
   * resolved `light` | `dark` value, never `system`.
   */
  theme?: ThemePreference;
  /** Time display format. */
  dateFormat?: '12h' | '24h';
  /** IANA timezone identifier, e.g. "America/New_York". */
  timezone?: string;
  /**
   * Human-readable default location for event discovery, as typed by the user
   * ("Charlotte, NC") or the coordinate string resolved from "Use current"
   * (e.g. "35.2271, -80.8431"). Display value only; resolved coordinates are
   * persisted separately onto the recommendation profile (homeLat/homeLon).
   */
  locationLabel?: string;
  /**
   * ISO 8601 timestamp of when the user finished the onboarding takeover
   * (FRE-801). Server-authoritative; absence means the user has not been
   * through onboarding yet and the gate should redirect them.
   */
  onboardingCompletedAt?: string;
}

// Pagination types
export interface PaginationParams {
  page?: number;
  limit?: number;
  offset?: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

// API Response types
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: ApiError;
  meta?: Record<string, any>;
}

export interface ApiError {
  code: string;
  message: string;
  details?: Record<string, any>;
  stack?: string;
}

// Location types
export interface Coordinates {
  lat: number;
  lng: number;
}

export interface Location extends Coordinates {
  address: string;
  city: string;
  state?: string;
  country: string;
  postalCode?: string;
}

// Event-related base types
export interface Event extends BaseEntity {
  title: string;
  description?: string;
  startDate: Date;
  endDate?: Date;
  location: Location;
  category: EventCategory;
  verified: boolean;
  featured: boolean;
  price?: EventPrice;
  capacity?: number;
  attendeeCount?: number;
  images?: string[];
  organizer: EventOrganizer;
  tags?: string[];
}

export interface EventPrice {
  min?: number;
  max?: number;
  currency: string;
  isFree: boolean;
}

export interface EventOrganizer extends BaseEntity {
  name: string;
  email: string;
  website?: string;
  verified: boolean;
}

/**
 * An event category: one of the closed ten top-level slugs (COR-37). An alias
 * of {@link CanonicalCategorySlug}, never a restatement — a hand-written legacy
 * six here made Discover drop five categories from every row (API-1719).
 */
export type EventCategory = CanonicalCategorySlug;

// Venue types
export interface Venue extends BaseEntity {
  name: string;
  address: string;
  city: string;
  state?: string;
  country: string;
  coordinates: Coordinates;
  capacity?: number;
  website?: string;
  phone?: string;
  verified: boolean;
}
